import type { NewPaymentPayload } from '@api/hooks/types/createTransaction';
import { CURRENCIES } from '@constants';
import type { Session } from '@ory/client';
import { applyAddressMode, isAddressEmpty } from '@pages/payments/utils/addressUtils';
import { format, parseISO } from 'date-fns';
import type {
	CorrespondentBank,
	CreatePaymentTemplate,
	IdType,
	TemplatePayer,
} from 'types/templates';
import { TemplatePaymentType, TemplateType } from 'types/templates';

export const mergeRefs =
	<T>(...refs: React.Ref<T>[]): React.RefCallback<T> =>
	(node) => {
		refs.forEach((ref) => {
			if (typeof ref === 'function') {
				ref(node);
			} else if (ref && 'current' in ref) {
				(ref as React.MutableRefObject<T | null>).current = node;
			}
		});
	};
export const getTime = (isoUtc: string): string => {
	const date = new Date(isoUtc);
	const hours = String(date.getHours()).padStart(2, '0');
	const minutes = String(date.getMinutes()).padStart(2, '0');
	return `${hours}:${minutes}`;
};

export const formatNumberAmount = (value: string | number): string => {
	const str = String(value).trim();
	const match = str.match(/^(-?)(\d+)(\.\d+)?$/);

	if (!match) {
		return str;
	}

	const [, sign, integerPart, decimalPart = ''] = match;
	// Normalize leading zeros (but keep single zero)
	const normalizedInteger = integerPart.replace(/^0+(?=\d)/, '');
	const formattedInteger = normalizedInteger.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

	return `${sign}${formattedInteger}${decimalPart}`;
};

export const formatWithCurrency = ({
	value,
	currency,
}: {
	value: number | string;
	currency: string;
}) => {
	return `${CURRENCIES?.[currency] ?? currency} ${formatNumberAmount(value)}`;
};

export interface TokenInfo {
	session?: Session;
	expireAt: number;
	issuedAt: number;
}

export const getInfoFromToken = (token?: string): TokenInfo => {
	if (!token) {
		return { expireAt: 0, issuedAt: 0 };
	}

	const payload = JSON.parse(atob(token.split('.')[1]));
	const { exp, iat, session } = payload ?? {};
	return { session: { ...session, tokenized: token }, expireAt: exp, issuedAt: iat };
};

export const cleanUpPayload = (data: NewPaymentPayload, hasAddressAligned?: boolean) => {
	const { ultimate_beneficiary, beneficiary } = data;
	if (
		ultimate_beneficiary &&
		!ultimate_beneficiary.account &&
		!ultimate_beneficiary.name &&
		!ultimate_beneficiary.address?.line &&
		!ultimate_beneficiary.address?.country &&
		!ultimate_beneficiary.id?.value &&
		!ultimate_beneficiary.id?.type
	) {
		delete data.ultimate_beneficiary;
	}

	if (ultimate_beneficiary?.name && !ultimate_beneficiary?.account) {
		delete data.ultimate_beneficiary?.account;
	}

	if (
		ultimate_beneficiary?.id &&
		!ultimate_beneficiary.id.type &&
		!ultimate_beneficiary.id.value
	) {
		delete data.ultimate_beneficiary?.id;
	}

	if (
		ultimate_beneficiary?.address &&
		!ultimate_beneficiary.address.line &&
		!ultimate_beneficiary.address.country
	) {
		delete data.ultimate_beneficiary?.address;
	}

	if (beneficiary && !beneficiary.address?.line && !beneficiary.address?.country) {
		delete data.beneficiary.address;
	}

	if (data.payment_type === 'INTERNATIONAL') {
		const { correspondent } = data;
		if (
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-expect-error
			correspondent?.length === 0 ||
			(correspondent &&
				!correspondent.bic &&
				!correspondent.name &&
				!correspondent.address.line &&
				!correspondent.address.country)
		) {
			delete data?.correspondent;
		}
	}

	if (data.payment_type === 'DOMESTIC' || data.payment_type === 'INTERNAL') {
		const { primary_payer } = data;
		if (primary_payer && !primary_payer.id?.value && !primary_payer.id?.value) {
			delete data?.primary_payer;
		}
	}

	if (beneficiary && !beneficiary.id?.value && !beneficiary.id?.type) {
		delete data.beneficiary.id;
	}

	if (data.payment_type === 'INTERNATIONAL' && !hasAddressAligned) {
		if (data.beneficiary?.address) {
			applyAddressMode(data.beneficiary.address);
		}
		if (data.ultimate_beneficiary?.address) {
			applyAddressMode(data.ultimate_beneficiary.address);
		}
		if (data.beneficiary?.bank?.address) {
			applyAddressMode(data.beneficiary.bank.address);
		}
		if (data.correspondent?.address) {
			applyAddressMode(data.correspondent.address);
		}
	}

	data.amount = Math.round((data.amount! * 10000) / 100);

	return data;
};

export const prepareTemplate = ({
	template_name,
	account_uuid,
	payment_type,
	amount,
	currency,
	payment_details,
	beneficiary,
	ultimate_beneficiary,
	correspondent,
	payer,
	primary_payer,
}: Omit<
	NewPaymentPayload & { template_name: string },
	'document_number' | 'execution_date'
> & {
	correspondent?: CorrespondentBank;
	payer?: IdType;
	primary_payer?: TemplatePayer;
}): CreatePaymentTemplate => {
	const base: CreatePaymentTemplate = {
		name: template_name,
		account_uuid,
		payment_type: payment_type as TemplatePaymentType,
		amount: amount || undefined,
		currency,
		payment_details,
		beneficiary,
		ultimate_beneficiary,
		correspondent,
		payer,
		primary_payer,
		customer_id: account_uuid,
		template_type: TemplateType.JOINT,
		payment_urgency: 'NORMAL',
	};

	return templateFormCleanUp(base);
};

export const prepareTemplateForPayment = (
	template: CreatePaymentTemplate,
): CreatePaymentTemplate => {
	// ToDo: remove after BE will remove account_id
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-expect-error
	if (template?.account_id) delete template.account_id;

	if (!template?.beneficiary?.address) {
		template.beneficiary = {
			...template.beneficiary,
			address: {
				type: 'STRUCTURED',
			},
		};
	}

	if (template.payment_type !== 'INTERNATIONAL') {
		if (!template?.ultimate_beneficiary?.address) {
			template.ultimate_beneficiary = {
				...template.ultimate_beneficiary,
				address: {
					type: 'STRUCTURED',
				},
			};
		}
	} else {
		if (!template?.beneficiary?.bank?.address) {
			template.beneficiary = {
				...template.beneficiary,
				bank: {
					...(template?.beneficiary?.bank ?? {}),
					address: {
						type: 'STRUCTURED',
					},
				},
			};
		}

		if (!template?.correspondent) {
			template.correspondent = {
				...template.correspondent,
				address: {
					type: 'STRUCTURED',
				},
			};
		}
	}

	return template;
};

export const templateFormCleanUp = (
	template: CreatePaymentTemplate,
	hasAddressAligned?: boolean,
) => {
	// ToDo: remove after BE will remove account_id
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-expect-error
	if (template?.account_id) delete template.account_id;

	const data: CreatePaymentTemplate = {
		...(nullsToUndefined(template) as CreatePaymentTemplate),
		template_type: TemplateType.JOINT,
		name: template.name.trim(),
		payment_urgency: 'NORMAL',
		amount: template?.amount ? template.amount * 100 : undefined,
	};

	if (!data.template_type) {
		data.template_type = TemplateType.JOINT;
	}

	if (Object.values(data?.ultimate_beneficiary?.id ?? {}).every((item) => !item)) {
		delete data.ultimate_beneficiary?.id;
	}

	const isInternationalTemplate = data.payment_type === TemplatePaymentType.INTERNATIONAL;

	if (isAddressEmpty(data?.ultimate_beneficiary?.address, hasAddressAligned)) {
		delete data.ultimate_beneficiary?.address;
	} else if (data?.ultimate_beneficiary?.address) {
		if (isInternationalTemplate) {
			applyAddressMode(data.ultimate_beneficiary.address, hasAddressAligned);
		}
	}

	if (Object.values(data?.ultimate_beneficiary ?? {}).every((item) => !item)) {
		delete data.ultimate_beneficiary;
	}

	if (Object.values(data?.beneficiary?.id ?? {}).every((item) => !item)) {
		delete data.beneficiary?.id;
	}

	if (data?.beneficiary?.bank?.address) {
		if (isAddressEmpty(data?.beneficiary?.bank?.address, hasAddressAligned)) {
			delete data.beneficiary.bank.address;
		} else {
			applyAddressMode(data.beneficiary.bank.address, hasAddressAligned);
		}
	}

	if (data?.beneficiary?.address) {
		if (isAddressEmpty(data?.beneficiary?.address, hasAddressAligned)) {
			delete data.beneficiary.address;
		} else if (isInternationalTemplate) {
			applyAddressMode(data.beneficiary.address, hasAddressAligned);
		} else {
			data.beneficiary.address.type = 'STRUCTURED';
		}
	}

	if (
		data?.beneficiary?.bank &&
		Object.values(data?.beneficiary?.bank ?? {}).every((item) => !item)
	) {
		delete data.beneficiary.bank;
	}

	if (Object.values(data?.beneficiary ?? {}).every((item) => !item)) {
		delete data.beneficiary;
	}

	if (Object.values(data?.primary_payer?.id ?? {}).every((item) => !item)) {
		delete data.primary_payer?.id;
	}

	if (Object.values(data?.primary_payer ?? {}).every((item) => !item)) {
		delete data.primary_payer;
	}

	if (Object.values(data?.payer ?? {}).every((item) => !item)) {
		delete data.payer;
	}

	if (Object.values(data?.payer ?? {}).every((item) => !item)) {
		delete data.payer;
	}

	if (isAddressEmpty(data?.correspondent?.address, hasAddressAligned)) {
		delete data.correspondent?.address;
	} else if (data?.correspondent?.address) {
		applyAddressMode(data.correspondent.address, hasAddressAligned);
	}

	if (
		!Object.keys(data?.correspondent ?? {}).length ||
		!Object.values(data?.correspondent ?? {}).every(Boolean)
	) {
		delete data.correspondent;
	}

	return data;
};

type AnyObject = Record<string, unknown>;

export const nullsToUndefined = (value: unknown): unknown => {
	if (value === null || value === '') {
		return undefined;
	}

	if (Array.isArray(value)) {
		return value.map(nullsToUndefined);
	}

	if (typeof value === 'object') {
		const result: AnyObject = {};

		for (const key in value) {
			result[key] = nullsToUndefined((value as AnyObject)[key]);
		}

		return result;
	}

	return value;
};

export const isGuruPayAppSession = (): boolean =>
	navigator.userAgent?.includes('GuruPayApp/') ?? false;

export const formatUtcDate = (dateStr: string): string => {
	const date = parseISO(dateStr);
	const utc = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
	return `${format(utc, 'yyyy-MM-dd | HH:mm:ss | ')}UTC`;
};
